import React, { useState, useEffect, useCallback } from 'react';

const EasterEggs = ({ children, totalGen }) => {
  const [solarFlare, setSolarFlare] = useState(false);
  const [starField, setStarField] = useState(false);
  const [solarSystemMode, setSolarSystemMode] = useState(false);
  const [retroMode, setRetroMode] = useState(false);
  const [keyBuffer, setKeyBuffer] = useState('');

  // Listen for keyboard input
  useEffect(() => {
    const handleKeyPress = (e) => {
      setKeyBuffer(prev => {
        const newBuffer = (prev + e.key.toLowerCase()).slice(-5);

        if (newBuffer === 'space') {
          setSolarSystemMode(true);
          return '';
        } else if (newBuffer.includes('1985')) {
          setRetroMode(current => !current);
          return '';
        }

        return newBuffer;
      });
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSolarSystemMode(false);
        setRetroMode(false);
        setKeyBuffer('');
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keypress', handleKeyPress);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <>
      {/* Solar Flare Animation */}
      {solarFlare && (
        <div className="fixed inset-0 pointer-events-none z-50">
          <div className="absolute inset-0 bg-yellow-400 opacity-0 animate-solar-flare" />
          {/* Particle burst */}
          {Array.from({ length: 30 }).map((_, i) => {
            const angle = (i / 30) * 360;
            return (
              <div
                key={`flare-${i}`}
                className="absolute w-2 h-2 bg-yellow-400 rounded-full"
                style={{
                  top: '50%',
                  left: '50%',
                  animation: `flare-particle-${i % 8} 1s ease-out forwards`,
                  animationDelay: `${i * 0.02}s`,
                  transform: `rotate(${angle}deg)`
                }}
              />
            );
          })}
        </div>
      )}

      {/* Star Field (Night Mode) */}
      {starField && (
        <div className="fixed inset-0 pointer-events-none z-40">
          {/* Stars */}
          {Array.from({ length: 100 }).map((_, i) => (
            <div
              key={`star-${i}`}
              className="absolute w-1 h-1 bg-white rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                opacity: 0.3 + Math.random() * 0.7,
                animation: `twinkle ${2 + Math.random() * 3}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 5}s`
              }}
            />
          ))}
          {/* Shooting stars */}
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={`shooting-${i}`}
              className="absolute w-1 h-1 bg-white rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 50}%`,
                boxShadow: '0 0 10px 2px rgba(255, 255, 255, 0.8)',
                animation: `shooting-star ${2 + Math.random()}s linear infinite`,
                animationDelay: `${i * 5}s`
              }}
            />
          ))}
        </div>
      )}

      {/* Solar System Mode Overlay */}
      {solarSystemMode && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
          onClick={() => setSolarSystemMode(false)}
        >
          <div className="relative w-full h-full max-w-6xl max-h-screen">
            <div className="absolute top-4 right-4 text-white text-sm">Press ESC or click anywhere to exit</div>

            {/* Sun in center */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="w-20 h-20 bg-yellow-400 rounded-full shadow-[0_0_60px_20px_rgba(251,191,36,0.6)]" />
            </div>

            {/* Orbiting planets (representing solar arrays) */}
            {[1, 2, 3, 4].map((planet, idx) => {
              const orbitRadius = 100 + (idx * 80);
              const speed = 10 + (idx * 5);
              const planetColors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b'];

              return (
                <div
                  key={`planet-${planet}`}
                  className="absolute"
                  style={{
                    top: '50%',
                    left: '50%',
                    width: `${orbitRadius * 2}px`,
                    height: `${orbitRadius * 2}px`,
                    marginLeft: `-${orbitRadius}px`,
                    marginTop: `-${orbitRadius}px`,
                    border: '1px dashed rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    animation: `orbit-${idx} ${speed}s linear infinite`
                  }}
                >
                  <div
                    className="absolute rounded-full"
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: planetColors[idx],
                      top: '-10px',
                      left: '50%',
                      marginLeft: '-10px',
                      boxShadow: `0 0 20px 5px ${planetColors[idx]}80`
                    }}
                  />
                </div>
              );
            })}

            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white text-center">
              <div className="text-2xl font-bold mb-2">Solar System</div>
              <div className="text-sm opacity-70">Each planet represents a solar array orbiting at speeds based on power generation</div>
            </div>
          </div>
        </div>
      )}

      {/* Retro Terminal Mode Overlay */}
      {retroMode && (
        <div className="fixed inset-0 bg-black z-50 retro-mode overflow-hidden flex items-center justify-center">
          <div className="scanlines" />
          <div className="retro-content p-4 font-mono text-green-400 text-xs leading-tight max-w-5xl">
            <div className="mb-1">╔═══════════════════════════════════════════════════════════════════════╗</div>
            <div className="mb-1">║              SOLAR ENERGY MONITORING SYSTEM v1.0 (1985)              ║</div>
            <div className="mb-1">║                    IBM PC COMPATIBLE - 640K RAM                       ║</div>
            <div className="mb-2">╚═══════════════════════════════════════════════════════════════════════╝</div>

            <div className="mb-1">SYSTEM BOOTING...</div>
            <div className="mb-0">[OK] Initializing solar array sensors</div>
            <div className="mb-0">[OK] Loading power conversion matrix</div>
            <div className="mb-0">[OK] Establishing data acquisition link</div>
            <div className="mb-2">[OK] System ready</div>

            <div className="mb-0">┌─────────────────────────────────────────────────────────────────────┐</div>
            <div className="mb-0">│ CURRENT READINGS                            {new Date().toLocaleTimeString('en-US', { hour12: false })} │</div>
            <div className="mb-0">├─────────────────────────────────────────────────────────────────────┤</div>
            <div className="mb-0">│ SYSTEM STATUS ................ {totalGen > 100 ? 'ACTIVE   ' : 'STANDBY '} │</div>
            <div className="mb-0">│ TOTAL POWER OUTPUT ........... {totalGen.toFixed(2).padStart(8)} W │</div>
            <div className="mb-0">│ POWER LEVEL .................. {totalGen > 1000 ? '████████  HIGH' : totalGen > 500 ? '████░░░░  MED ' : totalGen > 0 ? '██░░░░░░  LOW ' : '░░░░░░░░  ZERO'} │</div>
            <div className="mb-2">└─────────────────────────────────────────────────────────────────────┘</div>

            <div className="mb-0">┌─────────────────────────────────────────────────────────────────────┐</div>
            <div className="mb-0">│ SOLAR PANEL ARRAY DIAGRAM                                           │</div>
            <div className="mb-0">├─────────────────────────────────────────────────────────────────────┤</div>
            <div className="mb-0">│                                                                     │</div>
            <div className="mb-0">│   ┌─────┐  ┌─────┐  ┌─────┐  ┌─────┐  ┌─────┐  ┌─────┐  ┌─────┐  │</div>
            <div className="mb-0">│   │{totalGen > 0 ? '█████' : '░░░░░'}│  │{totalGen > 200 ? '█████' : '░░░░░'}│  │{totalGen > 400 ? '█████' : '░░░░░'}│  │{totalGen > 600 ? '█████' : '░░░░░'}│  │{totalGen > 800 ? '█████' : '░░░░░'}│  │{totalGen > 1000 ? '█████' : '░░░░░'}│  │{totalGen > 1200 ? '█████' : '░░░░░'}│  │</div>
            <div className="mb-0">│   │{totalGen > 0 ? '█████' : '░░░░░'}│  │{totalGen > 200 ? '█████' : '░░░░░'}│  │{totalGen > 400 ? '█████' : '░░░░░'}│  │{totalGen > 600 ? '█████' : '░░░░░'}│  │{totalGen > 800 ? '█████' : '░░░░░'}│  │{totalGen > 1000 ? '█████' : '░░░░░'}│  │{totalGen > 1200 ? '█████' : '░░░░░'}│  │</div>
            <div className="mb-0">│   │{totalGen > 0 ? '█████' : '░░░░░'}│  │{totalGen > 200 ? '█████' : '░░░░░'}│  │{totalGen > 400 ? '█████' : '░░░░░'}│  │{totalGen > 600 ? '█████' : '░░░░░'}│  │{totalGen > 800 ? '█████' : '░░░░░'}│  │{totalGen > 1000 ? '█████' : '░░░░░'}│  │{totalGen > 1200 ? '█████' : '░░░░░'}│  │</div>
            <div className="mb-0">│   └─────┘  └─────┘  └─────┘  └─────┘  └─────┘  └─────┘  └─────┘  │</div>
            <div className="mb-0">│                                                                     │</div>
            <div className="mb-0">│   ┌─────┐  ┌─────┐  ┌─────┐  ┌─────┐  ┌─────┐  ┌─────┐  ┌─────┐  │</div>
            <div className="mb-0">│   │{totalGen > 0 ? '█████' : '░░░░░'}│  │{totalGen > 200 ? '█████' : '░░░░░'}│  │{totalGen > 400 ? '█████' : '░░░░░'}│  │{totalGen > 600 ? '█████' : '░░░░░'}│  │{totalGen > 800 ? '█████' : '░░░░░'}│  │{totalGen > 1000 ? '█████' : '░░░░░'}│  │{totalGen > 1200 ? '█████' : '░░░░░'}│  │</div>
            <div className="mb-0">│   │{totalGen > 0 ? '█████' : '░░░░░'}│  │{totalGen > 200 ? '█████' : '░░░░░'}│  │{totalGen > 400 ? '█████' : '░░░░░'}│  │{totalGen > 600 ? '█████' : '░░░░░'}│  │{totalGen > 800 ? '█████' : '░░░░░'}│  │{totalGen > 1000 ? '█████' : '░░░░░'}│  │{totalGen > 1200 ? '█████' : '░░░░░'}│  │</div>
            <div className="mb-0">│   │{totalGen > 0 ? '█████' : '░░░░░'}│  │{totalGen > 200 ? '█████' : '░░░░░'}│  │{totalGen > 400 ? '█████' : '░░░░░'}│  │{totalGen > 600 ? '█████' : '░░░░░'}│  │{totalGen > 800 ? '█████' : '░░░░░'}│  │{totalGen > 1000 ? '█████' : '░░░░░'}│  │{totalGen > 1200 ? '█████' : '░░░░░'}│  │</div>
            <div className="mb-0">│   └─────┘  └─────┘  └─────┘  └─────┘  └─────┘  └─────┘  └─────┘  │</div>
            <div className="mb-2">└─────────────────────────────────────────────────────────────────────┘</div>

            <div className="mb-0">┌─────────────────────────────────────────────────────────────────────┐</div>
            <div className="mb-0">│ HISTORICAL DATA LOG                                                 │</div>
            <div className="mb-0">├─────────────────────────────────────────────────────────────────────┤</div>
            <div className="mb-0">│ TIMESTAMP        OUTPUT(W)  STATUS    EFFICIENCY                    │</div>
            <div className="mb-0">│ {new Date(Date.now() - 180000).toLocaleTimeString('en-US', { hour12: false })}     {(totalGen * 0.95).toFixed(2).padStart(7)}  NOMINAL   {totalGen > 0 ? '███░░ 85%' : '░░░░░  0%'}                   │</div>
            <div className="mb-0">│ {new Date(Date.now() - 120000).toLocaleTimeString('en-US', { hour12: false })}     {(totalGen * 0.98).toFixed(2).padStart(7)}  NOMINAL   {totalGen > 0 ? '████░ 92%' : '░░░░░  0%'}                   │</div>
            <div className="mb-0">│ {new Date(Date.now() - 60000).toLocaleTimeString('en-US', { hour12: false })}     {(totalGen * 1.02).toFixed(2).padStart(7)}  NOMINAL   {totalGen > 0 ? '█████ 98%' : '░░░░░  0%'}                   │</div>
            <div className="mb-0">│ {new Date().toLocaleTimeString('en-US', { hour12: false })}     {totalGen.toFixed(2).padStart(7)}  {totalGen > 100 ? 'ACTIVE ' : 'STANDBY'}   {totalGen > 0 ? '█████ 95%' : '░░░░░  0%'}                   │</div>
            <div className="mb-2">└─────────────────────────────────────────────────────────────────────┘</div>

            <div className="mb-0">COMMAND OPTIONS:</div>
            <div className="mb-0">  [1] View Detailed Statistics</div>
            <div className="mb-0">  [2] Print Report to LPT1</div>
            <div className="mb-0">  [3] Save Data to Floppy Drive A:</div>
            <div className="mb-0">  [ESC] Exit to MS-DOS</div>
            <div className="mb-1">  [1985] Toggle Modern Interface</div>

            <div className="mb-1 animate-pulse">C:\SOLAR&gt;_</div>

            <div className="text-xs opacity-50 text-center">
              (c) 1985 SolarMonitor Systems Inc. • Licensed to: BSD School District
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes solar-flare {
          0% { opacity: 0; }
          50% { opacity: 0.6; }
          100% { opacity: 0; }
        }
        @keyframes flare-particle-0 {
          0% { transform: translate(0, 0) scale(1); opacity: 1; }
          100% { transform: translate(200px, -100px) scale(0); opacity: 0; }
        }
        @keyframes flare-particle-1 {
          0% { transform: translate(0, 0) scale(1); opacity: 1; }
          100% { transform: translate(150px, 150px) scale(0); opacity: 0; }
        }
        @keyframes flare-particle-2 {
          0% { transform: translate(0, 0) scale(1); opacity: 1; }
          100% { transform: translate(-200px, 100px) scale(0); opacity: 0; }
        }
        @keyframes flare-particle-3 {
          0% { transform: translate(0, 0) scale(1); opacity: 1; }
          100% { transform: translate(-150px, -150px) scale(0); opacity: 0; }
        }
        @keyframes flare-particle-4 {
          0% { transform: translate(0, 0) scale(1); opacity: 1; }
          100% { transform: translate(0, -200px) scale(0); opacity: 0; }
        }
        @keyframes flare-particle-5 {
          0% { transform: translate(0, 0) scale(1); opacity: 1; }
          100% { transform: translate(0, 200px) scale(0); opacity: 0; }
        }
        @keyframes flare-particle-6 {
          0% { transform: translate(0, 0) scale(1); opacity: 1; }
          100% { transform: translate(200px, 0) scale(0); opacity: 0; }
        }
        @keyframes flare-particle-7 {
          0% { transform: translate(0, 0) scale(1); opacity: 1; }
          100% { transform: translate(-200px, 0) scale(0); opacity: 0; }
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.5); }
        }
        @keyframes shooting-star {
          0% { transform: translateX(0) translateY(0); opacity: 1; }
          70% { opacity: 1; }
          100% { transform: translateX(300px) translateY(300px); opacity: 0; }
        }
        @keyframes orbit-0 {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes orbit-1 {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes orbit-2 {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes orbit-3 {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-solar-flare {
          animation: solar-flare 1s ease-out;
        }
        .scanlines {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to bottom,
            rgba(255, 255, 255, 0) 50%,
            rgba(0, 255, 0, 0.1) 50%
          );
          background-size: 100% 4px;
          pointer-events: none;
          z-index: 1;
        }
        .retro-mode {
          text-shadow: 0 0 10px rgba(0, 255, 0, 0.8);
        }
        .retro-content {
          position: relative;
          z-index: 2;
        }
      `}</style>

      {/* Pass trigger functions to children via context or props */}
      {typeof children === 'function'
        ? children({
            triggerSolarFlare: () => {
              setSolarFlare(true);
              setTimeout(() => setSolarFlare(false), 1500);
            },
            toggleStarField: () => setStarField(!starField),
            starFieldActive: starField
          })
        : children
      }
    </>
  );
};

export default EasterEggs;
